import { get, useFormContext } from 'react-hook-form';
import type { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';

import type { JSX } from 'react';

/**
 * ラジオボタン基本コンポーネント
 */
export default function FormRadio<TFieldValues extends FieldValues = FieldValues>(props: {
  // タイトル
  subject?: string;
  // name属性
  name: string;
  // 選択オプション配列
  options: { value: string | number; label: string }[];
  // 必須検証の有無
  required?: boolean;
  // 必須/任意のアイコン表示有無
  requiredMark?: boolean;
  // 無効化
  disabled?: boolean;
  // 読み取り専用
  readOnly?: boolean;
  // ラジオボタンの配置方向
  direction?: 'horizontal' | 'vertical';
  errors: FieldErrors<TFieldValues>;
  // クラス名
  className?: string;
  // 値変更時のコールバック関数
  onChange?: (value: string | number) => void;

  register: UseFormRegister<TFieldValues>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  const formContext = useFormContext();
  const currentValue = formContext ? formContext.watch(props.name) : '';

  // 検証タイプの取得
  const type = get(props.errors, props.name)?.type ?? null;

  return (
    <>
      {props.subject && (
        <label className="form-label">
          {props.subject}
          {props.requiredMark && (
            <span className={props.required ? 'must' : 'any'}>{props.required ? ' *' : ''}</span>
          )}
        </label>
      )}
      <div
        className={`
          ${
            props.direction === 'horizontal'
              ? 'form-check-inline-group'
              : 'form-check-vertical-group'
          } 
          ${get(props.errors, props.name) ? 'is-invalid' : ''} 
          ${props.className || ''}
        `.trim()}
      >
        {props.options.map((item, index) => {
          return (
            <div
              key={item.label}
              className={`form-check ${
                props.direction === 'horizontal' ? 'form-check-inline' : ''
              }`}
              style={props.readOnly ? { pointerEvents: 'none', opacity: 0.6 } : {}}
            >
              <input
                className="form-check-input"
                type="radio"
                id={`${props.name}_${index}`}
                value={item.value}
                // FormProviderがある場合のみcheckedを設定、ない場合はregisterに任せる
                {...(formContext ? { checked: currentValue == item.value } : {})}
                readOnly={props.readOnly ? true : false}
                disabled={props.disabled ? true : false}
                {...props.register(props.name as Path<TFieldValues>, {
                  required: props.required,
                  onChange: e => {
                    if (props.onChange) {
                      props.onChange(e.target.value);
                    }
                  },
                })}
              />
              <label className="form-check-label" htmlFor={`${props.name}_${index}`}>
                {item.label}
              </label>
            </div>
          );
        })}
      </div>
      {get(props.errors, props.name) && (
        <p className="error">{type === 'required' && <>必須項目です。</>}</p>
      )}
      {type === 'custom' && <>{get(props.errors, props.name)?.message}</>}
    </>
  );
}
